/**
 * Org Service — piezas de UI compartidas por las páginas de /org: chips de estado y origen,
 * tabla de asignaciones (persona / cliente / solución / % / estado / período) y la ficha de
 * persona. Los nombres de solución salen de ORG_SOLUTIONS (shared), nunca de una lista local.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EnumBadge, type BadgeTone } from '@/components/ui/enum-badge';
import { InternalTable, EmptyState } from '@/components/internal/shared';
import { Card, CardContent } from '@/components/ui/card';
import type { RouterOutputs } from '@/lib/api-types';
import { solutionName } from './org-format';

export type OrgAssignment = RouterOutputs['org']['solution']['assignments'][number];
export type OrgPerson = RouterOutputs['org']['people'][number];

const STATUS_TONE: Record<string, BadgeTone> = { confirmed: 'success', suggested: 'warning', closed: 'muted' };
const ORIGIN_TONE: Record<string, BadgeTone> = { capacity: 'info', declared: 'accent', inferred: 'muted' };

export function OrgStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return <EnumBadge value={status} tone={STATUS_TONE[status] ?? 'neutral'} label={t(`org.status.${status}`, status)} />;
}

export function OrgOriginBadge({ origin }: { origin: string }) {
  const { t } = useTranslation();
  return <EnumBadge value={origin} tone={ORIGIN_TONE[origin] ?? 'neutral'} label={t(`org.origin.${origin}`, origin)} />;
}

export function OrgSolutionLink({ slug }: { slug: string | null | undefined }) {
  if (!slug) return <span className="text-muted-foreground">—</span>;
  return <Link className="hover:underline" to={`/org/soluciones/${slug}`}>{solutionName(slug)}</Link>;
}

export function OrgPersonLink({ email, name }: { email: string; name: string }) {
  return <Link className="font-medium hover:underline" to={`/org/personas/${encodeURIComponent(email)}`}>{name}</Link>;
}

export function OrgClientLink({ clientId, name }: { clientId: string; name: string }) {
  return <Link className="font-medium hover:underline" to={`/org/clientes/${clientId}`}>{name}</Link>;
}

const fmtPct = (pct: number | null) => (pct === null || pct === undefined ? '—' : `${Math.round(pct)} %`);

/** Tabla de asignaciones. `hide` saca columnas redundantes según la página (la ficha de persona no repite a la persona). */
export function OrgAssignmentsTable({ rows, hide = [] }: { rows: readonly OrgAssignment[]; hide?: Array<'person' | 'client' | 'solution'> }) {
  const { t } = useTranslation();
  if (!rows.length) return <EmptyState title={t('org.table.empty', 'Sin asignaciones vigentes.')} />;
  const columns = [
    ...(hide.includes('person') ? [] : [{ key: 'person', label: t('org.table.person', 'Persona'), render: (r: OrgAssignment) => <OrgPersonLink email={r.email} name={r.full_name} /> }]),
    ...(hide.includes('client') ? [] : [{ key: 'client', label: t('org.table.client', 'Cliente'), render: (r: OrgAssignment) => <OrgClientLink clientId={r.client_id} name={r.display_name} /> }]),
    ...(hide.includes('solution') ? [] : [{ key: 'solution', label: t('org.table.solution', 'Solución'), render: (r: OrgAssignment) => <OrgSolutionLink slug={r.solution_slug} /> }]),
    { key: 'role', label: t('org.table.role', 'Puesto'), render: (r: OrgAssignment) => <span className="text-muted-foreground">{r.role_title ?? '—'}</span> },
    { key: 'pct', label: t('org.table.pct', 'Dedicación'), align: 'right' as const, render: (r: OrgAssignment) => fmtPct(r.pct) },
    { key: 'status', label: t('org.table.status', 'Estado'), render: (r: OrgAssignment) => <OrgStatusBadge status={r.status} /> },
    { key: 'origin', label: t('org.table.origin', 'Origen'), render: (r: OrgAssignment) => <OrgOriginBadge origin={r.origin} /> },
    { key: 'period', label: t('org.table.period', 'Mes'), render: (r: OrgAssignment) => <span className="text-muted-foreground">{r.period}</span> },
  ];
  return <InternalTable columns={columns} rows={rows} rowKey={(r) => r.id} />;
}

export function OrgPersonCard({ person, solutionSlug, leader }: {
  person: { full_name: string; email: string; role_title: string | null; area: string | null; team: string | null; status: string };
  solutionSlug: string | null;
  leader: { email: string; name: string } | null;
}) {
  const { t } = useTranslation();
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex gap-3 text-sm"><span className="w-28 shrink-0 text-muted-foreground">{label}</span><span className="min-w-0">{children}</span></div>
  );
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{person.full_name}</h2>
          {person.status !== 'Activo' && <EnumBadge value={person.status} tone="muted" />}
        </div>
        <Row label={t('org.card.role', 'Puesto')}>{person.role_title ?? '—'}</Row>
        <Row label={t('org.card.area', 'Área')}>{[person.area, person.team].filter(Boolean).join(' · ') || '—'}</Row>
        <Row label={t('org.card.solution', 'Solución')}>{solutionSlug ? <OrgSolutionLink slug={solutionSlug} /> : <span className="text-muted-foreground">{t('org.card.no_solution', 'Sin solución asignada')}</span>}</Row>
        <Row label={t('org.card.leader', 'Líder')}>{leader ? <OrgPersonLink email={leader.email} name={leader.name} /> : <span className="text-muted-foreground">{t('org.card.no_leader', 'Sin líder en el Community Map')}</span>}</Row>
        <Row label={t('org.card.email', 'Mail')}><span className="text-muted-foreground">{person.email}</span></Row>
      </CardContent>
    </Card>
  );
}
